import { z } from 'zod';

// Cannot use instanceof, because users might use different
// Zod version, this utility helps with that.
const isOfType = (element, type) => element.constructor.name === type.name;
const defaultZodValueGetterMap = new Map();
// Simple types
defaultZodValueGetterMap.set(z.ZodBoolean.name, () => false);
defaultZodValueGetterMap.set(z.ZodNumber.name, () => 0);
defaultZodValueGetterMap.set(z.ZodString.name, () => '');
defaultZodValueGetterMap.set(z.ZodArray.name, () => []);
defaultZodValueGetterMap.set(z.ZodRecord.name, () => ({}));
// Custom default value
defaultZodValueGetterMap.set(z.ZodDefault.name, (f) => f._def.defaultValue());
// Effects is just a wrapper of another type
defaultZodValueGetterMap.set(z.ZodEffects.name, (f) => getSchemaDefaultForField(f._def.schema));
// Optional might have a default value
defaultZodValueGetterMap.set(z.ZodOptional.name, (f) => isOfType(f._def.innerType, z.ZodDefault)
    ? f._def.innerType._def.defaultValue()
    : undefined);
// Tuples are combination of multiple types
defaultZodValueGetterMap.set(z.ZodTuple.name, (f) => {
    const tuple = [];
    for (const item of f._def.items) {
        tuple.push(getSchemaDefaultForField(item));
    }
    return tuple;
});
// All object types should recursively call getSchemaDefaultObject()
defaultZodValueGetterMap.set(z.ZodEffects.name, (f) => getSchemaDefaultForField(f._def.schema));
defaultZodValueGetterMap.set(z.ZodUnion.name, (f) => getSchemaDefaultForField(f._def.options[0]));
defaultZodValueGetterMap.set(z.ZodObject.name, (f) => getSchemaDefaultForObject(f));
defaultZodValueGetterMap.set(z.ZodRecord.name, (f) => getSchemaDefaultForObject(f));
defaultZodValueGetterMap.set(z.ZodIntersection.name, (f) => getSchemaDefaultForObject(f));
/**
 * Based on the provided type of the field, returns either the defined
 * default value (if any) or a default "empty" value.
 * @param field The field of a Zod schema for which to retrieve a default value.
 * @returns The default value based on the type of the field provided.
 */
function getSchemaDefaultForField(field) {
    const typeKey = field.constructor.name;
    if (!defaultZodValueGetterMap.has(typeKey)) {
        console.warn('getSchemaDefaultForField: Unhandled type', field.constructor.name);
        return undefined;
    }
    const getter = defaultZodValueGetterMap.get(typeKey);
    // TODO: Fix the typing to avoid the ts-expect-error
    // @ts-expect-error Dynamic map of types is throwing TypeScript
    return getter(field);
}
/**
 * Get the default values of an object-like field.
 * Recursively-called on object-like fields.
 *
 * @param schema The schema for which to retrieve the default values.
 * @returns An object matching the schema with default "empty" values.
 */
function getSchemaDefaultForObject(schema) {
    if (isOfType(schema, z.ZodRecord)) {
        return {};
    }
    if (isOfType(schema, z.ZodIntersection)) {
        return Object.assign(Object.assign({}, getSchemaDefaultForObject(schema._def.left)), getSchemaDefaultForObject(schema._def.right));
    }
    if (isOfType(schema, z.ZodUnion)) {
        for (const option of schema._def.options) {
            if (isOfType(option, z.ZodObject)) {
                return getSchemaDefaultForObject(option);
            }
        }
        console.warn('getSchemaDefaultObject: No object found in union, returning empty object');
        return {};
    }
    if (!isOfType(schema, z.ZodObject)) {
        console.warn(`getSchemaDefaultObject: Expected object schema, got ${schema.constructor.name}`);
        return {};
    }
    return Object.fromEntries(Object.entries(schema.shape).map(([key, field]) => [key, getSchemaDefaultForField(field)]));
}
/**
 * Returns the default values as an object for the provided schema.
 *
 * For example, given the following schema:
 * ```typescript
 * const schema = z.object({
 *   name: z.string(),
 *   age: z.number(),
 *   isStudent: z.boolean().default(true),
 * });
 * ```
 *
 * The default object would be:
 * ```json
 * {
 *   name: "",
 *   age: 0,
 *   isStudent: true
 * }
 * ```
 * Note the default value for `isStudent` is `true` because it is specified in
 * the schema.
 *
 * @param schema
 */
function getDefaultsForSchema(schema) {
    return getSchemaDefaultForObject(schema);
}

export { getDefaultsForSchema };
